import React from 'react';
import { Link } from 'react-router-dom';
import { isEmpty, pick, } from 'lodash';
import { updateDoc, limit, query, where, orderBy, collection, doc, } from 'firebase/firestore';
import { httpsCallable, } from 'firebase/functions';
import { format as formatDate } from 'date-fns';
import numeral from 'numeral';
import { keyBy, groupBy } from 'lodash';
import { toast } from 'react-toastify';
import qs from 'qs';

import firebase, { db, functions, getCollectionData, } from '../../firebase';
import { fields, } from '../../shared/models/adApiAccount';
import useQueryParams from '../hooks/useQueryParams';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import ModelFormModal from '../modals/ModelFormModal';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import AddButton from '../AddButton';
import ProgressButton from '../ProgressButton';
import TenantPage from '../TenantPage';
import useTenant from '../hooks/useTenant';
import SettingsTabs from '../SettingsTabs';

const getAdApiAuthUrl = httpsCallable(functions, 'getAdApiAuthUrl');
const pullAdProfiles = httpsCallable(functions, 'pullAdProfiles');

export default function AdApiAccounts (props) {
  return (
    <TenantPage>
      <PageContent {...props} />
    </TenantPage>
  );
};

function PageContent (props) {
  const { tenant, history, location, } = props;
  const queryParams = useQueryParams();
  const adApiAccounts = useCollectionSubscription(query(collection(tenant.ref, 'adApiAccounts')));
  const adApiAccountsById = keyBy(adApiAccounts, 'id');
  const sellers = useCollectionSubscription(query(collection(tenant.ref, 'sellers')));
  const sellersGroupedByAdApiAccountId = groupBy(sellers, 'adApiAccountId');
  const onSubmitAuth = async ({ adApiAccountId }) => {
    const adApiAccount = adApiAccountsById[adApiAccountId];
    await updateDoc(adApiAccount.ref, { adApi: pick(queryParams, ['accessToken', 'refreshToken', 'expires', 'userName', 'userEmail']) });

    try {
      const { data: profiles } = await pullAdProfiles({ tenantId: tenant.id, adApiAccountId });
      const linkedCount = (profiles || []).filter(_ => _.linkedSellerId != null).length;
      toast.success(`認証し、${linkedCount}件のセラーと連携しました。広告データは翌朝以降に反映されます`);
    } catch(e) {
      console.error(e);
      toast.error('認証はできましたが、セラー連携に失敗しました');
    }

    history.replace('/settings/adApiAccounts');
  };
  const onClickAuthAdApi = async () => {
    const { data: url } = await getAdApiAuthUrl();
    window.location.href = url;
  };

  return (
    <div className="ad-api-accounts container-fluid">
      <div className="px-4">
        <div className="card p-4">
          <SettingsTabs activeTab="adApiAccounts" />
          {tenant.hasDemo ? (
            <div className="alert alert-info mb-3">デモ中はこの設定を変更できません。トライアル開始後に設定できます。</div>
          ) : (
            <>
              <div className="alert alert-info small">
                広告データは1日1回・早朝に自動で取り込まれるため、認証直後は数値が表示されません。認証の翌朝以降に反映されます。
              </div>
              <div className="mb-3 d-flex justify-content-start gap-1">
                <ProgressButton process={onClickAuthAdApi}>
                  認証する
                </ProgressButton>
                <AddButton itemRef={doc(collection(tenant.ref, 'adApiAccounts'))} FormModal={ModelFormModal} formProps={{ title: '広告API認証アカウント 追加', fields: fields(), }} />
              </div>
            </>
          )}
          <table className="table table-bordered">
            <thead className="thead-light text-nowrap">
              <tr>
                <th style={{ minWidth: 250 }}>名称</th>
                <th>認証状態</th>
                <th style={{ minWidth: 220 }}>認証アカウント</th>
                <th style={{ minWidth: 200 }}>連携セラー</th>
                <th style={{ minWidth: 70 }}></th>
              </tr>
            </thead>
            <tbody>
              {
                adApiAccounts.map(({ id, ref, name, adApi, }) => {
                  const beforeDelete = async () => {
                    if((await getCollectionData(query(collection(tenant.ref, 'sellers'), where('adApiAccountId', '==', id), limit(1)))).length > 0) {
                      toast.error('使用されているため削除できません');
                      return false;
                    }
                  };
                  const onClickPullProfiles = async () => {
                    if(!window.confirm('広告APIアカウントにひもづくプロファイルを取得し、セラーと連携します。よろしいですか？')) return;

                    try {
                      const { data: profiles } = await pullAdProfiles({ tenantId: tenant.id, adApiAccountId: id });
                      const linkedCount = (profiles || []).filter(_ => _.linkedSellerId != null).length;
                      toast.success(`${linkedCount}件のセラーと連携しました`);
                    } catch(e) {
                      console.error(e);
                      toast.error('失敗しました');
                    }
                  };

                  const linkedSellers = sellersGroupedByAdApiAccountId[id] || [];

                  return (
                    <tr key={id} id={id}>
                      <td>
                        {name}
                      </td>
                      <td>
                        {adApi?.refreshToken != null ? <div className="text-success">認証済み</div> : <div className="text-danger">未認証</div>}
                      </td>
                      <td>
                        {
                          adApi?.userEmail != null || adApi?.userName != null ? (
                            <>
                              {adApi?.userName && <div>{adApi.userName}</div>}
                              {adApi?.userEmail && <div className="small text-muted">{adApi.userEmail}</div>}
                            </>
                          ) : (
                            adApi?.refreshToken != null
                              ? <span className="small text-muted">再認証で表示されます</span>
                              : <span className="text-muted">—</span>
                          )
                        }
                      </td>
                      <td>
                        {
                          linkedSellers.length > 0
                            ? linkedSellers.map(_ => _.name || _.sellerId).join(', ')
                            : <span className="text-danger"><span className="fas fa-exclamation-triangle me-1" />連携セラーなし</span>
                        }
                      </td>
                      <td>
                        {!tenant.hasDemo && (
                          <div className="text-nowrap d-flex justify-content-end gap-1">
                            <ProgressButton process={onClickPullProfiles} disabled={adApi == null}>
                              セラーと連携する
                            </ProgressButton>
                            <EditButton itemRef={ref} FormModal={ModelFormModal} formProps={{ title: '広告API認証アカウント 編集', fields: fields(), }} />
                            <DeleteButton itemRef={ref} beforeDelete={beforeDelete} />
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
        {
          ['refreshToken'].every(_ => queryParams[_]) && (
            <ModelFormModal
              isOpen
              title="広告APIアカウント"
              renderFormHeader={() => (
                <div className="alert alert-success">
                  <div className="fw-bold mb-1">
                    <span className="fas fa-user-check me-1" />
                    このAmazonアカウントで認証しました
                  </div>
                  {queryParams.userName && <div>氏名: {queryParams.userName}</div>}
                  {queryParams.userEmail && <div>メール: {queryParams.userEmail}</div>}
                  <div className="small mt-1 text-muted">
                    連携したい広告アカウントと、上記のログインアカウントが一致しているかご確認ください。違う場合は「閉じる」で中止し、正しいアカウントで認証し直してください。
                  </div>
                </div>
              )}
              fields={{
                adApiAccountId: {
                  type: 'select',
                  options: adApiAccounts.map(_ => ({ value: _.id, label: _.name, })),
                  validations: {
                    required: v => !isEmpty(v),
                  },
                },
              }}
              submitLabel="完了"
              onSubmit={onSubmitAuth}
              onClickClose={_ => history.replace(location.pathname)}
            />
          )
        }
      </div>
    </div>
  );
};
